'use client';

import type {
  IconButtonProps,
  MenuItemProps,
  DividerProps,
  PopperPlacementType,
} from '@mui/material';
import {
  ClickAwayListener,
  Fade,
  Paper,
  Popper,
  Divider,
  IconButton,
  MenuItem,
  MenuList,
  Stack,
} from '@mui/material';
import classnames from 'classnames';
import { createRef, useCallback, useRef, useState } from 'react';
import type { ReactNode, RefObject, SyntheticEvent } from 'react';

import { Icon } from '@/components/shared/icon';

import { GroupTitle } from './groupTitle';

import { AuthorizationTooltip } from '../tooltips/authorizationTooltip';

export interface OptionMenuItemType {
  id: string;
  groupHeader?: boolean;
  divider?: boolean;
  dividerProps?: DividerProps;
  icon?: ReactNode;
  text?: ReactNode;
  menuItemProps?: MenuItemProps;
  predicate?: () => boolean;
  isAuthorized?: boolean;
  isFeatureSupported?: boolean;
  subMenu?: OptionType[];
  disabledTooltip?: string;
}

export type OptionType = string | OptionMenuItemType;

interface OptionsMenuType {
  icon?: ReactNode;
  iconClassName?: string;
  options: OptionType[];
  leftAlignMenu?: boolean;
  iconButtonProps?: IconButtonProps;
  onToggle?: (open: boolean) => void;
  onOptionSelected?: (option: OptionType) => void;
  Component?: React.ElementType;
  componentProps?: Record<string, unknown>;
  popperClassName?: string;
}

const InnerMenu = (
  props: OptionsMenuType & {
    open: boolean;
    placement: PopperPlacementType;
    closeMenu: () => void;
    anchorRef: React.RefObject<HTMLElement | null>;
    enablePropagation?: boolean;
  }
) => {
  const {
    onOptionSelected,
    closeMenu,
    open,
    options,
    anchorRef,
    placement,
    popperClassName,
  } = props;
  const innerAnchorsRef = useRef<RefObject<HTMLLIElement | null>[]>([]);
  innerAnchorsRef.current = options.map(
    (_, i) => innerAnchorsRef.current[i] ?? createRef<HTMLLIElement[]>()
  );
  const [subMenuOpen, setSubMenuOpen] = useState<number>();
  const handleClose = useCallback(
    (event: Event | SyntheticEvent) => {
      if (anchorRef.current?.contains(event.target as HTMLElement)) {
        return;
      }
      setSubMenuOpen(undefined);
      closeMenu();
    },
    [anchorRef, closeMenu]
  );
  const handleSelect = useCallback(
    (option: OptionType, e: Event | SyntheticEvent) => {
      if (onOptionSelected) onOptionSelected(option);
      handleClose(e);
    },
    [onOptionSelected, handleClose]
  );

  const filteredOptions = options.filter((o) =>
    typeof o === 'object' && 'predicate' in o ? o.predicate?.() : true
  );

  return (
    <Popper
      open={open}
      anchorEl={anchorRef.current}
      placement={placement}
      transition
      disablePortal
      className={popperClassName}
      sx={{ zIndex: 5 }}
    >
      {({ TransitionProps }) => (
        <Fade {...TransitionProps}>
          <Paper className={'shadow-lg'}>
            <ClickAwayListener onClickAway={handleClose}>
              <MenuList
                autoFocusItem={open}
                onClick={
                  props.enablePropagation
                    ? undefined
                    : (e) => e.stopPropagation()
                }
              >
                {filteredOptions.map((option: OptionType, index: number) => {
                  if (typeof option === 'string') {
                    return (
                      <MenuItem
                        key={option}
                        data-testid={option}
                        sx={{ minWidth: '150px' }}
                        onClick={(e) => {
                          handleSelect(option, e);
                        }}
                      >
                        {option}
                      </MenuItem>
                    );
                  } else if ('divider' in option) {
                    const previousOption = filteredOptions[index - 1];
                    if (
                      index === 0 ||
                      (typeof previousOption !== 'string' &&
                        'divider' in previousOption)
                    ) {
                      return null;
                    }
                    return (
                      option.divider && (
                        <Divider
                          key={option.id}
                          {...option.dividerProps}
                          className='my-[8px]'
                        />
                      )
                    );
                  } else if ('groupHeader' in option) {
                    return <GroupTitle key={option.id} title={option.text} />;
                  } else {
                    return (
                      <AuthorizationTooltip
                        isAuthorized={option.isAuthorized}
                        isFeatureSupported={option.isFeatureSupported}
                        title={
                          option.menuItemProps?.disabled
                            ? option.disabledTooltip || undefined
                            : undefined
                        }
                        key={option.id}
                      >
                        <MenuItem
                          {...option.menuItemProps}
                          ref={innerAnchorsRef.current[index]}
                          onMouseEnter={(e) => {
                            setSubMenuOpen(index);
                            option.menuItemProps?.onMouseEnter?.(e);
                          }}
                          onMouseLeave={(e) => {
                            setSubMenuOpen(undefined);
                            option.menuItemProps?.onMouseLeave?.(e);
                          }}
                          onClick={(e) => {
                            handleSelect(option, e);
                            option.menuItemProps?.onClick?.(e);
                          }}
                          data-testid={option.id}
                        >
                          <Option
                            icon={option.icon}
                            text={option.text}
                            isSubMenu={!!option.subMenu}
                          />
                          {option.subMenu && (
                            <InnerMenu
                              {...props}
                              anchorRef={innerAnchorsRef.current[index]}
                              options={option.subMenu}
                              placement='right-start'
                              open={subMenuOpen === index}
                              closeMenu={() => setSubMenuOpen(undefined)}
                              enablePropagation
                            />
                          )}
                        </MenuItem>
                      </AuthorizationTooltip>
                    );
                  }
                })}
              </MenuList>
            </ClickAwayListener>
          </Paper>
        </Fade>
      )}
    </Popper>
  );
};

const OptionMenu = (props: OptionsMenuType) => {
  const {
    icon = 'material-symbols-more-horiz',
    iconClassName,
    iconButtonProps,
    onToggle,
    Component,
    componentProps,
  } = props;
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const handleToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setOpen((prevOpen) => !prevOpen);
    onToggle?.(!open);
  };

  if (
    props.options.length === 0 ||
    props.options.every(
      (obj) =>
        typeof obj === 'object' && 'predicate' in obj && !obj.predicate?.()
    )
  ) {
    return null;
  }

  return (
    <>
      {Component && (
        <Component
          {...componentProps}
          data-testid='option-menu-button'
          ref={anchorRef}
          onClick={handleToggle}
        />
      )}
      {!Component && (
        <IconButton
          disableRipple
          ref={anchorRef}
          size='small'
          data-testid='option-menu-button'
          onClick={handleToggle}
          {...iconButtonProps}
        >
          {typeof icon === 'string' ? (
            <i
              className={classnames(icon, 'text-textPrimary', iconClassName)}
            />
          ) : (
            icon
          )}
        </IconButton>
      )}
      <InnerMenu
        {...props}
        placement={props.leftAlignMenu ? 'bottom-start' : 'bottom-end'}
        open={open}
        closeMenu={() => {
          setOpen(false);
          onToggle?.(false);
        }}
        anchorRef={anchorRef}
      />
    </>
  );
};

export const menuItemStyles = (isSelected: boolean, isDark: boolean) => {
  return {
    height: '36px',
    padding: '4px 16px 4px 16px',
    borderRadius: 0,

    ...(isSelected
      ? {
          backgroundColor: 'var(--mui-palette-primary-main)',
          color: 'var(--mui-palette-background-default)',
        }
      : {}),

    '&:hover': {
      backgroundColor: isSelected
        ? 'var(--mui-palette-primary-main)'
        : isDark
          ? '#1A2027'
          : '#E7F0F3',
    },
  };
};

export default OptionMenu;

const Option = ({
  icon,
  text,
  isSubMenu,
}: {
  icon?: ReactNode;
  text: ReactNode;
  isSubMenu?: boolean;
}) => {
  return (
    <Stack
      direction='row'
      alignItems='center'
      justifyContent='space-between'
      width='100%'
      gap='4px'
    >
      <Stack direction='row' alignItems='center' gap='8px' marginRight='8px'>
        {(typeof icon === 'string' ? (
          <Icon
            iconClass={icon}
            sx={{
              '& i': {
                color: 'currentColor',
              },
            }}
          />
        ) : (
          icon
        )) || null}
        {text}
      </Stack>
      {isSubMenu && (
        <Icon
          iconClass='material-symbols-chevron-right-rounded'
          sx={{
            padding: '0px',
            '& i': {
              margin: '0px',
              color: 'currentColor',
            },
          }}
        />
      )}
    </Stack>
  );
};
